import { Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
  updateDoc,
  where,
} from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { forkJoin, from, map, Observable, Subject, switchMap, of } from "rxjs";
import { ICategory } from "../interfaces/category.interface";

export type ReviewPurchaseAction = "approve" | "reject";

@Injectable({
  providedIn: "root",
})
export class CashbackService {
  private itemsPerPage = 5;
  private lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private firstDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private lastVisibleDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  hasNextPage = false;
  hasPreviousPage = false;

  /** Fallback quando o índice composto companyId+dateTime ainda não existe. */
  private useClientPagination = false;
  private clientDocs: DocumentData[] = [];
  private clientPageIndex = 0;

  private collectionRef;
  private collectionName = "cashback";
  private fieldSort = "dateTime";

  private actionSource = new Subject<string>();
  action$ = this.actionSource.asObservable();

  constructor(
    private firestore: Firestore,
    private functions: Functions
  ) {
    this.collectionRef = collection(this.firestore, this.collectionName);
  }

  sendAction(action: string) {
    this.actionSource.next(action);
  }

  async create(category: ICategory) {
    const docRef = await addDoc(this.collectionRef, category);
    return await updateDoc(docRef, { id: docRef.id });
  }

  async update(id: string, data: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, data);
  }

  async delete(id: string) {
    const docRef = doc(this.firestore, this.collectionName, id);
    await deleteDoc(docRef);
    this.sendAction("delete");
  }

  /** Aprova/rejeita compra via Cloud Function (mesma regra do mobile). */
  async reviewPurchase(cashbackId: string, action: ReviewPurchaseAction) {
    const callable = httpsCallable(this.functions, "reviewPurchase");
    await callable({ cashbackId, action });
    this.sendAction(action === "approve" ? "approved" : "rejected");
  }

  /** @deprecated Preferir reviewPurchase('approve'). Mantido por compatibilidade. */
  async updateApproved(id: string, _data?: { aprovado?: boolean }) {
    await this.reviewPurchase(id, "approve");
  }

  isPendente(item: { aprovado?: boolean; rejeitado?: boolean }): boolean {
    return !item?.aprovado && !item?.rejeitado;
  }

  statusLabel(item: { aprovado?: boolean; rejeitado?: boolean }): string {
    if (item?.aprovado) return "Aprovado";
    if (item?.rejeitado) return "Rejeitado";
    return "Pendente";
  }

  statusBadgeClass(item: { aprovado?: boolean; rejeitado?: boolean }): string {
    if (item?.aprovado) return "badge-success";
    if (item?.rejeitado) return "badge-danger";
    return "badge-warning";
  }

  statusIconClass(item: { aprovado?: boolean; rejeitado?: boolean }): string {
    if (item?.aprovado) return "ni-check-bold";
    if (item?.rejeitado) return "ni-fat-remove";
    return "ni-time-alarm";
  }

  rowClass(item: { aprovado?: boolean; rejeitado?: boolean }): string {
    if (item?.aprovado) return "bg-success-light";
    if (item?.rejeitado) return "bg-danger-light";
    return "bg-warning-light";
  }

  async getById(id: string) {
    const docRef = doc(this.firestore, this.collectionName, id);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return snapshot.data();
    } else {
      return null;
    }
  }

  private async enrichWithCustomerData(cashbackList: any[]): Promise<any[]> {
    if (cashbackList.length === 0) return [];

    const customerIds = [
      ...new Set(cashbackList.map((item: any) => item.customerId)),
    ].filter((id) => id);

    if (customerIds.length === 0) return cashbackList;

    const customerQueries = customerIds.map((uid) => {
      const customerRef = collection(this.firestore, "customers");
      const q = query(customerRef, where("uid", "==", uid), limit(1));

      return from(getDocs(q)).pipe(
        map((snapshot) => {
          if (!snapshot.empty) {
            const customerDoc = snapshot.docs[0];
            return {
              uid: uid,
              id: customerDoc.id,
              ...customerDoc.data(),
            };
          }
          return {
            uid: uid,
            id: null,
          };
        })
      );
    });

    try {
      const result = await forkJoin(customerQueries)
        .pipe(
          map((customers) => {
            const customerMap = new Map(
              customers.map((customer) => [customer.uid, customer])
            );

            return cashbackList.map((cashback: any) => ({
              ...cashback,
              customer: customerMap.get(cashback.customerId) || null,
            }));
          })
        )
        .toPromise();

      return result || cashbackList;
    } catch {
      return cashbackList.map((cashback: any) => ({
        ...cashback,
        customer: null,
      }));
    }
  }

  private dateTimeMillis(value: any): number {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    if (value.seconds != null) return value.seconds * 1000;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private async pageFromClientCache(): Promise<DocumentData[]> {
    const start = this.clientPageIndex * this.itemsPerPage;
    const page = this.clientDocs.slice(start, start + this.itemsPerPage);
    this.hasPreviousPage = this.clientPageIndex > 0;
    this.hasNextPage = start + this.itemsPerPage < this.clientDocs.length;
    return (await this.enrichWithCustomerData(page)) || [];
  }

  private async loadClientFallback(companyId: string): Promise<DocumentData[]> {
    this.useClientPagination = true;
    const colRef = collection(this.firestore, this.collectionName);
    const snapshot = await getDocs(
      query(colRef, where("companyId", "==", companyId))
    );

    this.clientDocs = snapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .sort(
        (a: any, b: any) =>
          this.dateTimeMillis(b.dateTime) - this.dateTimeMillis(a.dateTime)
      );
    this.clientPageIndex = 0;

    if (this.clientDocs.length === 0) {
      this.updatePaginationState([], false, false);
      return [];
    }

    return this.pageFromClientCache();
  }

  private isMissingIndexError(error: any): boolean {
    const message = String(error?.message ?? error ?? "");
    return (
      error?.code === "failed-precondition" ||
      message.includes("requires an index") ||
      message.includes("The query requires an index")
    );
  }

  getFirstPage(): Observable<DocumentData[]> {
    const companyId = localStorage.getItem("_companyId");
    if (!companyId) return of([]);

    this.resetPagination();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      where("companyId", "==", companyId),
      orderBy(this.fieldSort, "desc"),
      limit(this.itemsPerPage)
    );

    return from(
      getDocs(q).catch(async (error) => {
        if (this.isMissingIndexError(error)) {
          return null;
        }
        throw error;
      })
    ).pipe(
      switchMap(async (snapshot) => {
        if (snapshot === null) {
          return this.loadClientFallback(companyId);
        }

        if (snapshot.empty) {
          this.updatePaginationState([], false, false);
          return [];
        }

        this.firstDoc = snapshot.docs[0];
        this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
        this.lastVisibleDocs = [this.firstDoc];

        const cashbackList = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        this.updatePaginationState(
          snapshot.docs,
          false,
          snapshot.docs.length === this.itemsPerPage
        );

        return (await this.enrichWithCustomerData(cashbackList)) || [];
      })
    );
  }

  getNextPage(): Observable<DocumentData[]> {
    if (this.useClientPagination) {
      if (!this.hasNextPage) return of([]);
      this.clientPageIndex += 1;
      return from(this.pageFromClientCache());
    }

    if (!this.lastDoc || !this.hasNextPage) return of([]);

    const companyId = localStorage.getItem("_companyId");
    if (!companyId) return of([]);

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      where("companyId", "==", companyId),
      orderBy(this.fieldSort, "desc"),
      startAfter(this.lastDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      switchMap(async (snapshot) => {
        if (snapshot.empty) {
          this.hasNextPage = false;
          return [];
        }

        this.firstDoc = snapshot.docs[0];
        this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
        this.lastVisibleDocs.push(this.firstDoc);

        const cashbackList = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        this.updatePaginationState(
          snapshot.docs,
          true,
          snapshot.docs.length === this.itemsPerPage
        );

        return (await this.enrichWithCustomerData(cashbackList)) || [];
      })
    );
  }

  getPreviousPage(): Observable<DocumentData[]> {
    if (this.useClientPagination) {
      if (!this.hasPreviousPage) return of([]);
      this.clientPageIndex -= 1;
      return from(this.pageFromClientCache());
    }

    if (this.lastVisibleDocs.length < 2 || !this.hasPreviousPage) return of([]);

    const companyId = localStorage.getItem("_companyId");
    if (!companyId) return of([]);

    const previousDoc = this.lastVisibleDocs[this.lastVisibleDocs.length - 2];
    this.lastVisibleDocs.pop();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      where("companyId", "==", companyId),
      orderBy(this.fieldSort, "desc"),
      startAt(previousDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      switchMap(async (snapshot) => {
        if (snapshot.empty) {
          return [];
        }

        this.firstDoc = snapshot.docs[0];
        this.lastDoc = snapshot.docs[snapshot.docs.length - 1];

        const cashbackList = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        this.updatePaginationState(
          snapshot.docs,
          this.lastVisibleDocs.length > 0,
          true
        );

        return (await this.enrichWithCustomerData(cashbackList)) || [];
      })
    );
  }

  private resetPagination() {
    this.lastDoc = null;
    this.firstDoc = null;
    this.lastVisibleDocs = [];
    this.hasNextPage = false;
    this.hasPreviousPage = false;
    this.useClientPagination = false;
    this.clientDocs = [];
    this.clientPageIndex = 0;
  }

  private updatePaginationState(
    docs: QueryDocumentSnapshot<DocumentData>[],
    hasPrevious: boolean,
    hasNext: boolean
  ) {
    this.hasPreviousPage = hasPrevious;
    this.hasNextPage = hasNext;
  }
}
